import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShieldCheck, Lock, RotateCcw, Truck } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../state/auth';
import { useCart } from '../state/cart';
import ShippingAddressFields from '../components/ShippingAddressFields';
import PayFirstProof from '../components/PayFirstProof';
import { trackAddToCart, trackInitiateCheckout, getFbCookies } from '../lib/pixel';
import { trackEvent } from '../lib/track';
import { loadLeadContact } from '../lib/leadContact';
import { useT, useLocalField } from '../i18n';

const EMPTY_SHIPPING = {
  fullName: '', phone: '', city: '', neighborhood: '',
  address: '', landmark: '', postalCode: '',
};

export default function Checkout() {
  const t = useT();
  const lf = useLocalField();
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { user, applyToken, isStaff } = useAuth();
  const { saveGuestItem, removeGuestItemByShareToken } = useCart();

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Prefill from the contact captured at the pre-preview step (same-device
  // localStorage; see lib/leadContact). A logged-in user's account email still
  // wins for the email field. `name`/`phone` seed the shipping recipient + phone.
  const [email, setEmail] = useState(() => user?.email || loadLeadContact()?.email || '');
  const [ship, setShip] = useState(() => {
    const lead = loadLeadContact();
    return { ...EMPTY_SHIPPING, fullName: lead?.name || '', phone: lead?.phone || '' };
  });

  useEffect(() => {
    api.get(`/api/stories/preview/${shareToken}`)
      .then(setStory)
      .catch(() => setStory(null))
      .finally(() => setLoading(false));
  }, [shareToken]);

  // Buy-funnel analytics: the checkout page was actually reached.
  useEffect(() => {
    trackEvent('checkout_view', { slug: shareToken });
  }, [shareToken]);

  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user, email]);

  if (loading) return <div className="loader"><div className="loader-spinner" /></div>;
  if (!story) return (
    <div className="error-card">
      <h2>{t.templateNotFound}</h2>
    </div>
  );

  const { template, personalization } = story;
  const variantSku = personalization?.variantSku;
  const variant = template?.variants?.find(v => v.sku === variantSku)
    || template?.variants?.find(v => v.isActive);

  const variantLabels = { softcover: t.softcover, hardcover: t.hardcover, digital: t.digital };
  const isPhysical = variant?.type === 'softcover' || variant?.type === 'hardcover';

  // Delivery is charged on physical orders only — mirrors the server's
  // create-session logic so the buyer sees the exact total Stripe will charge.
  const subtotalCents = variant?.priceCents || 0;
  const deliveryCents = isPhysical ? (story.delivery?.amountCents || 0) : 0;
  const orderTotalCents = subtotalCents + deliveryCents;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    trackEvent('checkout_submit', { slug: shareToken });
    try {
      const body = {
        shareToken,
        email,
        variantSku: variant?.sku,
        shippingAddress: isPhysical ? { ...ship, country: 'إسرائيل' } : {},
        // Meta attribution signals — the server stores these and forwards them
        // on the Conversions API Purchase (deduped with the browser pixel).
        ...getFbCookies(),
        eventSourceUrl: window.location.href,
      };
      const resp = await api.post('/api/checkout/create-session', body);
      // New email → the server created an account and returned a session token:
      // log the browser in and continue, and drop the local guest entry (the
      // server pending order is the cart now). An existing email is never
      // auto-logged-in (it must sign in with its password), so it keeps the
      // local guest cart instead.
      if (resp.loginToken) {
        try { await applyToken(resp.loginToken); } catch { /* token is still stored */ }
        removeGuestItemByShareToken(shareToken);
      } else if (!user) {
        saveGuestItem({
          shareToken,
          nameAr: template?.nameAr,
          nameHe: template?.nameHe,
          variantSku: variant?.sku,
          variantType: variant?.type,
          priceCents: variant?.priceCents,
          personalization: { name: personalization?.name },
        });
      }
      // Fire AddToCart here (not at story generation) so the event represents a
      // real buying intent: the buyer has entered their full address and a
      // checkout session was created, and is about to land on the payment page.
      // Our ad campaigns optimize on AddToCart, so this is the signal we want.
      // The server already sent the reliable copy of both events via CAPI (so
      // they survive this redirect); eventID makes Meta dedupe the two into one.
      trackAddToCart({
        id: template?.slug,
        name: lf(template || {}, 'nameAr'),
        value: variant ? subtotalCents / 100 : undefined,
        eventId: resp.atcEventId,
      });
      trackInitiateCheckout({
        id: template?.slug,
        value: variant ? orderTotalCents / 100 : undefined,
        eventId: resp.icEventId,
      });
      window.location.href = resp.sessionUrl;
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <div className="checkout-page">
      <form className="checkout-form" onSubmit={handleSubmit}>
        <h2>{t.checkoutTitle}</h2>

        <label className="field-label">{t.email}</label>
        <input type="email" name="email" autoComplete="email" className="field-input" value={email}
          onChange={e => setEmail(e.target.value)} required />

        {isPhysical && (
          <ShippingAddressFields value={ship} onChange={setShip} />
        )}

        {error && <p className="error-text">{error}</p>}

        {isStaff && (
          <p className="admin-free-notice">{t.adminFreeNotice}</p>
        )}

        {isPhysical && (
          <div className="checkout-delivery-estimate">
            <Truck size={18} aria-hidden="true" />
            <span>{t.checkoutDeliveryEstimate}</span>
          </div>
        )}

        <button type="submit" className="submit-btn" disabled={submitting}>
          {submitting ? t.checkoutProcessing : (isStaff ? t.adminFreeBtn : t.checkoutPay)}
        </button>

        {/* Same customer-conversation proof video as the pay-first checkout,
            right under the pay button for the hesitant buyer. */}
        <div className="checkout-proof">
          <PayFirstProof />
        </div>

        <div className="checkout-trust">
          <div className="checkout-trust-item">
            <Lock size={16} />
            <span>{t.checkoutTrustSsl}</span>
          </div>
          <div className="checkout-trust-item">
            <ShieldCheck size={16} />
            <span>{t.checkoutTrustStripe}</span>
          </div>
       
        </div>
      </form>

      <div className="checkout-summary">
        <h3>{t.checkoutOrderSummary}</h3>
        <div className="checkout-summary-item">
          <span>{lf(template || {}, 'nameAr')}</span>
          {personalization?.name && <span className="checkout-child-name">{personalization.name}</span>}
        </div>
        {variant && (
          <>
            <div className="checkout-summary-variant">
              {variantLabels[variant.type] || variant.type}
            </div>
            {deliveryCents > 0 && (
              <>
                <div className="checkout-summary-row">
                  <span>{t.subtotal}</span>
                  <span>
                    {variant.compareAtCents > variant.priceCents && (
                      <span className="price-was">{(variant.compareAtCents / 100).toFixed(0)}{t.currency}</span>
                    )}
                    {(subtotalCents / 100).toFixed(0)}{t.currency}
                  </span>
                </div>
                <div className="checkout-summary-row">
                  <span>{t.delivery}</span>
                  <span>{(deliveryCents / 100).toFixed(0)}{t.currency}</span>
                </div>
              </>
            )}
            <div className="checkout-summary-total">
              {t.total}: {(orderTotalCents / 100).toFixed(0)}{t.currency}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
